import { Component, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { sha1 } from 'object-hash';
import { BehaviorSubject, iif, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

export type DefaultOption = {
  label: string,
  value: string,
  [key: string]: any // additional util fields
}

@Component({
  selector: 'om-select',
  templateUrl: './om-select.component.html',
  styleUrls: ['./om-select.component.scss']
})
export class OmSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control: FormControl | undefined = undefined;
  @Input() disabled: boolean = false;
  @Input() mode: 'multiple' | 'tags' | 'default' = 'default';
  @Input() clear: boolean = false;
  @Input() class: string = 'w-full';

  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() icon: string = '';

  @Input() options: any[] = [];
  @Input() valueName: string = 'value';
  @Input() labelName: string = 'label';

  @Input() subLabelName: string = '';
  @Input() searchKeys: string[] = [];
  @Input() searchable: boolean = false;
  @Input() serverSearch: boolean = false;
  @Input() hideValidators: boolean = false;
  @Input() emitModelChange: boolean = false;


  @Output() modelChange = new EventEmitter<string>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() scrollToBottom = new EventEmitter<void>();

  isRequired$ = new BehaviorSubject<boolean>(false);

  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.control = this.control ?? new FormControl(this.mode === 'default' ? '' : []);
    this.isRequired$.next(this.control.hasValidator(Validators.required));

    // Single reactive stream: debounce only when doing server search
    iif(
      () => this.serverSearch,
      this.search$.pipe(debounceTime(300), distinctUntilChanged()),
      this.search$,
    ).pipe(
      takeUntil(this.destroy$),
    ).subscribe(term => this.searchChange.emit(term));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.control && !changes.control.firstChange && !!changes.control.currentValue) {      
      if (changes.control.currentValue.hasValidator(Validators.required)) {
        this.isRequired$.next(true);
      } else {
        this.isRequired$.next(false);
      }
    }
  }

  compare(o1: any, o2: any) {
    if (o1 === o2) return true;
    if (typeof o1 !== typeof o2) return false;
    if (typeof o1 !== 'object' || o1 === null || o2 === null) return o1 === o2;

    // Per oggetti semplici, confronto shallow
    if (o1.value !== undefined && o2.value !== undefined) {
      return o1.value === o2.value;
    }

    return sha1(o1) === sha1(o2);
  }

  customFilter = (search: string, option: any): boolean => {
    const term = (search ?? '').trim().toLowerCase();
    if (!term) return true;
    if (this.searchKeys.length) {
      const item = this.options.find(o => o[this.valueName] === option.nzValue) ?? option;
      return this.searchKeys.some(key => String(item[key] ?? '').toLowerCase().includes(term));
    }
    return !!option.nzLabel?.toLowerCase().includes(term);
  };

  get showSearch(): boolean {
    return this.searchable || this.serverSearch;
  }

  trackByValue = (index: number, item: any) => item[this.valueName] || index;

  /** Called by template (nzOnSearch) — always feeds the reactive stream */
  onSearchInput(term: string): void {
    this.search$.next(term);
  }

  /** Called when dropdown opens/closes — triggers empty search on open for server-search selects */
  onOpenChange(isOpen: boolean): void {
    if (isOpen && this.serverSearch) {
      this.searchChange.emit('');
    }
  }

  emitEvent(): void {
    if (!this.emitModelChange || !this.control) return;
    this.modelChange.emit(this.control.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
